import React, { useEffect, useState, useContext } from "react";
import useBreadcrumbs from "../../utils/hooks/useBreadcrumbs";
import { RiDashboardFill } from "react-icons/ri";
import DashboardService from "../../services/api/DashboardService";
import BookingService from "../../services/api/BookingService";
import OrderService from "../../services/api/OrderService";
import { DashboardStats, AnalyticsData, Booking, Order } from "../../Types/Dashboard";
import StatCards from "./components/StatCards";
import ChartsSection from "./components/ChartsSection";
import RecentActivity from "./components/RecentActivity";
import TopPerformers from "./components/TopPerformers";
import { NotifyContext } from "../../context/notifyContext";
import Alert from "../../UI/Alert/Alert";

export default function Dashboard() {
  useBreadcrumbs([
    {
      label: "Tableau de bord",
      icon: <RiDashboardFill />,
      is_link: false,
      href: "/app/dashboard",
    },
  ]);

  const notify = useContext(NotifyContext);
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [analytics, setAnalytics] = useState<AnalyticsData | null>(null);
  const [recentBookings, setRecentBookings] = useState<Booking[]>([]);
  const [recentOrders, setRecentOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Fetch stats
      const statsResponse = await DashboardService.getStats();
      setStats(statsResponse.data);

      // Fetch analytics
      const analyticsResponse = await DashboardService.getAnalytics(30);
      setAnalytics(analyticsResponse.data);

      // Fetch recent bookings
      const bookingsResponse = await BookingService.getBookings({
        page: 1,
        per_page: 5,
      });
      setRecentBookings(bookingsResponse.data?.bookings || []);

      // Fetch recent orders
      const ordersResponse = await OrderService.getOrders({
        page: 1,
        per_page: 5,
      });
      setRecentOrders(ordersResponse.data?.orders || []);
    } catch (err: any) {
      const errorMessage = err?.message || "Erreur lors du chargement du dashboard";
      setError(errorMessage);
      notify(errorMessage, "error");
    } finally {
      setLoading(false);
    }
  };

  if (error) {
    return (
      <div className="p-6">
        <Alert color="error">{error}</Alert>
        <button
          onClick={fetchDashboardData}
          className="mt-4 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
        >
          Réessayer
        </button>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-800">Tableau de bord</h1>
        <p className="text-gray-600 mt-1">
          Vue d'ensemble de l'activité de la plateforme
        </p>
      </div>

      {stats && <StatCards stats={stats} loading={loading} />}

      {analytics && (
        <>
          <ChartsSection analytics={analytics} loading={loading} />
          <TopPerformers analytics={analytics} loading={loading} />
        </>
      )}

      <RecentActivity
        recentBookings={recentBookings}
        recentOrders={recentOrders}
        loading={loading}
      />
    </div>
  );
}
